import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, CheckCircle, XCircle, Clock, RefreshCw, Mail, AlertCircle, Loader2, Sparkles } from 'lucide-react';

interface ValidationResult {
  valid: boolean;
  reason: string | null;
}

interface ValidationResults {
  mail_cname: ValidationResult;
  dkim1: ValidationResult;
  dkim2: ValidationResult;
}

interface FailedRecord {
  type: string;
  reason: string;
}

interface CheckResult {
  checkPerformed: boolean;
  validationResults: ValidationResults | null;
  failedRecords: FailedRecord[];
  sgCheckError: string | null;
}

interface DomainData {
  verified: boolean;
  customDomain: string;
  user: {
    businessName: string;
    email: string;
  };
  dnsRecords: {
    mail_cname: { host: string; data: string } | null;
    dkim1: { host: string; data: string } | null;
    dkim2: { host: string; data: string } | null;
  };
  verificationCount: number;
  checkPerformed?: boolean;
  validationResults?: ValidationResults | null;
  failedRecords?: FailedRecord[];
  sgCheckError?: string | null;
}

export function DomainSetup() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [domainData, setDomainData] = useState<DomainData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [rateLimitRemaining, setRateLimitRemaining] = useState<number | null>(null);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);

  const loadDomainData = useCallback(async (showVerifying = false) => {
    if (!token) {
      setError('Geen geldige token');
      setLoading(false);
      return;
    }

    if (showVerifying) {
      setVerifying(true);
      setCheckResult(null);
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-domain-public`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          },
          body: JSON.stringify({ token, checkNow: showVerifying }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429 && data.remainingSeconds) {
          setRateLimitRemaining(data.remainingSeconds);
          setError(null);
          return;
        }
        throw new Error(data.error || 'Kon domeingegevens niet ophalen');
      }

      setDomainData(data);
      setError(null);
      setRateLimitRemaining(null);

      if (showVerifying && data.checkPerformed) {
        setCheckResult({
          checkPerformed: true,
          validationResults: data.validationResults ?? null,
          failedRecords: data.failedRecords ?? [],
          sgCheckError: data.sgCheckError ?? null,
        });
      }
    } catch (err: any) {
      console.error('Error loading domain data:', err);
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    loadDomainData();
  }, [loadDomainData]);

  // Auto-refresh countdown timer
  useEffect(() => {
    if (rateLimitRemaining && rateLimitRemaining > 0) {
      const timer = setInterval(() => {
        setRateLimitRemaining((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(timer);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [rateLimitRemaining]);

  const handleCopy = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleVerify = () => {
    loadDomainData(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="mb-6">
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/697a293834ed484d86c80d4b_dibbi.png"
              alt="dibbi avatar"
              className="w-20 h-20 mx-auto shadow-lg rounded-2xl"
            />
          </div>
          <Loader2 className="w-8 h-8 text-[#FF6B35] mx-auto animate-spin mb-4" />
          <p className="text-lg font-medium text-gray-700">Gegevens ophalen...</p>
        </div>
      </div>
    );
  }

  if (error && !domainData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="mb-6">
              <img
                src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/697a293834ed484d86c80d4b_dibbi.png"
                alt="dibbi avatar"
                className="w-20 h-20 mx-auto shadow-lg rounded-2xl"
              />
            </div>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Oeps!</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-blue-900">Hulp nodig?</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Neem contact op met ons support team via{' '}
                    <a href="mailto:support@dibbi.nl" className="font-medium underline hover:text-blue-800">
                      support@dibbi.nl
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasEmptyRecords = !domainData?.dnsRecords.mail_cname?.host ||
                           !domainData?.dnsRecords.dkim1?.host ||
                           !domainData?.dnsRecords.dkim2?.host;

  const recordLabels: Record<string, string> = {
    mail_cname: 'CNAME Record (Mail)',
    dkim1: 'CNAME Record (DKIM 1)',
    dkim2: 'CNAME Record (DKIM 2)',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-[#FF6B35] to-[#ff5722] p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/697a293834ed484d86c80d4b_dibbi.png"
                  alt="dibbi avatar"
                  className="w-12 h-12 rounded-xl shadow-md"
                />
                <h1 className="text-3xl font-bold">DNS Setup</h1>
              </div>
              <p className="text-white/90 text-lg">
                Voor {domainData?.user.businessName || 'je domein'}
              </p>
            </div>
          </div>

          <div className="p-8">
            {domainData?.verified ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-green-900 mb-2 flex items-center gap-2">
                      Gelukt! <Sparkles className="w-5 h-5 text-green-600" />
                    </h2>
                    <p className="text-green-800 leading-relaxed">
                      De DNS records voor <span className="font-bold">{domainData.customDomain}</span> zijn succesvol geverifieerd.
                      Ons support team is automatisch op de hoogte gesteld en gaat nu aan de slag met de activatie!
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {hasEmptyRecords && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-7 h-7 text-red-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-red-900 mb-2">
                          DNS records ontbreken
                        </h2>
                        <p className="text-red-800 leading-relaxed">
                          Er zijn geen DNS records gevonden voor je domein. Neem contact op met ons support team via{' '}
                          <a href="mailto:support@dibbi.nl" className="font-semibold underline hover:text-red-900">
                            support@dibbi.nl
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {!hasEmptyRecords && (
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-7 h-7 text-yellow-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-yellow-900 mb-2">
                          Wacht op verificatie
                        </h2>
                        <p className="text-yellow-800 leading-relaxed">
                          De DNS records zijn nog niet geverifieerd. Zorg ervoor dat alle onderstaande records correct zijn
                          ingesteld in je DNS configuratie en klik dan op "Controleer DNS Records".
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="space-y-6">
              {!hasEmptyRecords && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">DNS Records</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Voeg deze DNS records toe aan je domein <span className="font-bold text-[#FF6B35]">{domainData?.customDomain}</span>:
                  </p>

                  <div className="space-y-4">
                    {domainData?.dnsRecords.mail_cname && domainData.dnsRecords.mail_cname.host && (
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 hover:border-[#FF6B35] transition-colors shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-bold text-gray-900">CNAME Record (Mail)</h4>
                          <div className="flex items-center gap-2">
                            {checkResult?.validationResults && (
                              checkResult.validationResults.mail_cname.valid
                                ? <CheckCircle className="w-5 h-5 text-green-500" />
                                : <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-1 rounded">CNAME</span>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Host/Name:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.mail_cname.host}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('mail_host', domainData.dnsRecords.mail_cname!.host)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'mail_host' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Value/Points to:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.mail_cname.data}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('mail_data', domainData.dnsRecords.mail_cname!.data)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'mail_data' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          {checkResult?.validationResults && !checkResult.validationResults.mail_cname.valid && checkResult.validationResults.mail_cname.reason && (
                            <p className="text-xs text-red-600 font-medium mt-1">{checkResult.validationResults.mail_cname.reason}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {domainData?.dnsRecords.dkim1 && domainData.dnsRecords.dkim1.host && (
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 hover:border-[#FF6B35] transition-colors shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-bold text-gray-900">CNAME Record (DKIM 1)</h4>
                          <div className="flex items-center gap-2">
                            {checkResult?.validationResults && (
                              checkResult.validationResults.dkim1.valid
                                ? <CheckCircle className="w-5 h-5 text-green-500" />
                                : <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-1 rounded">CNAME</span>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Host/Name:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.dkim1.host}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('dkim1_host', domainData.dnsRecords.dkim1!.host)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'dkim1_host' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Value/Points to:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.dkim1.data}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('dkim1_data', domainData.dnsRecords.dkim1!.data)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'dkim1_data' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          {checkResult?.validationResults && !checkResult.validationResults.dkim1.valid && checkResult.validationResults.dkim1.reason && (
                            <p className="text-xs text-red-600 font-medium mt-1">{checkResult.validationResults.dkim1.reason}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {domainData?.dnsRecords.dkim2 && domainData.dnsRecords.dkim2.host && (
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 hover:border-[#FF6B35] transition-colors shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-bold text-gray-900">CNAME Record (DKIM 2)</h4>
                          <div className="flex items-center gap-2">
                            {checkResult?.validationResults && (
                              checkResult.validationResults.dkim2.valid
                                ? <CheckCircle className="w-5 h-5 text-green-500" />
                                : <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-1 rounded">CNAME</span>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Host/Name:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.dkim2.host}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('dkim2_host', domainData.dnsRecords.dkim2!.host)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'dkim2_host' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-2">Value/Points to:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={domainData.dnsRecords.dkim2.data}
                                readOnly
                                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                                onClick={(e) => e.currentTarget.select()}
                              />
                              <button
                                onClick={() => handleCopy('dkim2_data', domainData.dnsRecords.dkim2!.data)}
                                className="px-4 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#ff5722] transition-all hover:shadow-lg active:scale-95"
                                title="Kopieer"
                              >
                                {copiedField === 'dkim2_data' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                          {checkResult?.validationResults && !checkResult.validationResults.dkim2.valid && checkResult.validationResults.dkim2.reason && (
                            <p className="text-xs text-red-600 font-medium mt-1">{checkResult.validationResults.dkim2.reason}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!domainData?.verified && !hasEmptyRecords && (
                <div className="pt-6 border-t-2 border-gray-200 mt-8">
                  <button
                    onClick={handleVerify}
                    disabled={verifying || !!rateLimitRemaining}
                    className="w-full bg-gradient-to-r from-[#FF6B35] to-[#ff5722] text-white py-4 px-6 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 disabled:from-gray-400 disabled:to-gray-500 group"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Controleren bij SendGrid...
                      </>
                    ) : rateLimitRemaining ? (
                      <>
                        <Clock className="w-6 h-6" />
                        Even geduld... ({rateLimitRemaining}s)
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-6 h-6 group-hover:rotate-180 transition-transform duration-500" />
                        Controleer DNS Records
                      </>
                    )}
                  </button>

                  {rateLimitRemaining && (
                    <div className="mt-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-yellow-900 mb-1">Even geduld...</p>
                          <p className="text-sm text-yellow-700">
                            Je kunt maximaal 1 keer per minuut controleren. De knop wordt automatisch weer actief over <span className="font-bold">{rateLimitRemaining} seconden</span>.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {checkResult?.checkPerformed && !checkResult.sgCheckError && checkResult.validationResults && (
                    <div className={`mt-4 rounded-xl p-5 shadow-sm border-2 ${
                      checkResult.failedRecords.length === 0
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        {checkResult.failedRecords.length === 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          {checkResult.failedRecords.length === 0 ? (
                            <p className="text-sm font-semibold text-green-900">Alle records zijn correct ingesteld!</p>
                          ) : (
                            <>
                              <p className="text-sm font-semibold text-red-900 mb-3">
                                {checkResult.failedRecords.length === 3
                                  ? 'Geen van de DNS records is nog gevonden.'
                                  : `${checkResult.failedRecords.length} van de 3 records zijn nog niet correct.`}
                              </p>
                              <div className="space-y-2">
                                {(['mail_cname', 'dkim1', 'dkim2'] as const).map((key) => {
                                  const result = checkResult.validationResults![key];
                                  return (
                                    <div key={key} className="flex items-center gap-2">
                                      {result.valid
                                        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                        : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                      }
                                      <span className={`text-sm font-medium ${result.valid ? 'text-green-800' : 'text-red-800'}`}>
                                        {recordLabels[key]}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-red-700 mt-3">
                                DNS wijzigingen kunnen tot 24-48 uur nodig hebben om te propageren. Probeer het later opnieuw.
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {checkResult?.sgCheckError && (
                    <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 font-medium">{checkResult.sgCheckError}</p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-8 border-t-2 border-gray-200 mt-8">
                <div className="bg-gradient-to-r from-blue-50 to-sky-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-blue-900 mb-2">Hulp nodig?</h3>
                      <p className="text-sm text-blue-700 leading-relaxed mb-3">
                        Heb je vragen of lukt het niet? Neem gerust contact op met ons support team via{' '}
                        <a href="mailto:support@dibbi.nl" className="font-bold underline hover:text-blue-900 transition-colors">
                          support@dibbi.nl
                        </a>
                      </p>
                      {domainData?.verificationCount ? (
                        <p className="text-xs text-blue-600 font-medium bg-blue-100 inline-block px-3 py-1 rounded-full">
                          Controle pogingen: {domainData.verificationCount}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-8">
          <div className="inline-flex items-center gap-3">
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
              alt="dibbi logo"
              className="h-8"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
